import { useMutation, useQuery } from "convex/react";
import {
  ArrowClockwise,
  Briefcase,
  FirstAidKit,
  PlugsConnected,
  ShieldCheck,
  SignOut,
  WarningOctagon,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  SupportAuditEvent,
  SupportConversationMetadata,
  SupportOverviewItem,
  SupportRecoveryItem,
} from "./types";

type SupportOperatorView = {
  active: boolean;
  displayName: string | null;
  email: string | null;
};

const emptyOverview: SupportOverviewItem[] = [];
const emptyConversations: SupportConversationMetadata[] = [];
const emptyRecoveryItems: SupportRecoveryItem[] = [];
const emptyAuditEvents: SupportAuditEvent[] = [];

function queryArgs<T extends Record<string, unknown>>(
  enabled: boolean,
  args: T,
) {
  return enabled ? args : "skip";
}

function activeBusinessQueryArgs(
  activeBusinessId: Id<"businesses"> | null,
  isActive: boolean,
) {
  if (!isActive || !activeBusinessId) return "skip";

  return { businessId: activeBusinessId };
}

function activeConversationQueryArgs(
  activeBusinessId: Id<"businesses"> | null,
  isActive: boolean,
) {
  if (!isActive || !activeBusinessId) return "skip";

  return { businessId: activeBusinessId, limit: 12 };
}

function activeAuditQueryArgs(
  activeBusinessId: Id<"businesses"> | null,
  isActive: boolean,
) {
  if (!isActive || !activeBusinessId) return "skip";

  return { businessId: activeBusinessId, limit: 8 };
}

function formatShort(value: number | null) {
  return value ? new Date(value).toLocaleString() : "None";
}

function recoveryLabel(item: SupportRecoveryItem) {
  return `${item.targetKind.replaceAll("_", " ")} ${item.targetId.slice(-6)}`;
}

function makeRequestKey(prefix: string, item: SupportRecoveryItem) {
  return [
    "nk35-ui",
    prefix,
    item.targetKind.toLowerCase(),
    item.targetId,
    crypto.randomUUID(),
  ].join(":");
}

function operatorName(operator: SupportOperatorView) {
  return operator.displayName ?? operator.email ?? "Support operator";
}

function resolveActiveBusinessId(
  overview: SupportOverviewItem[],
  selectedBusinessId: Id<"businesses"> | null,
) {
  const selected = overview.find(
    (item) => item.business.businessId === selectedBusinessId,
  );

  return (
    selected?.business.businessId ?? overview[0]?.business.businessId ?? null
  );
}

function useSupportData(isActive: boolean) {
  const overviewResult = useQuery(
    api.support.getConsoleOverview,
    queryArgs(isActive, { limit: 25 }),
  );
  const overview = (overviewResult ?? emptyOverview) as SupportOverviewItem[];
  const [selectedBusinessId, setSelectedBusinessId] =
    useState<Id<"businesses"> | null>(null);
  const activeBusinessId = resolveActiveBusinessId(
    overview,
    selectedBusinessId,
  );
  const snapshot = useQuery(
    api.support.getBusinessSupportSnapshot,
    activeBusinessQueryArgs(activeBusinessId, isActive),
  ) as SupportOverviewItem | undefined;
  const conversationsResult = useQuery(
    api.support.listConversationMetadata,
    activeConversationQueryArgs(activeBusinessId, isActive),
  );
  const recoveryItemsResult = useQuery(
    api.support.listRecoveryItems,
    activeBusinessQueryArgs(activeBusinessId, isActive),
  );
  const auditEventsResult = useQuery(
    api.support.listSupportAuditTrail,
    activeAuditQueryArgs(activeBusinessId, isActive),
  );
  const selectedSummary = useMemo(
    () =>
      snapshot ??
      overview.find((item) => item.business.businessId === activeBusinessId),
    [activeBusinessId, overview, snapshot],
  );

  return {
    activeBusinessId,
    auditEvents: (auditEventsResult ?? emptyAuditEvents) as SupportAuditEvent[],
    conversations: (conversationsResult ??
      emptyConversations) as SupportConversationMetadata[],
    overview,
    recoveryItems: (recoveryItemsResult ??
      emptyRecoveryItems) as SupportRecoveryItem[],
    selectedSummary,
    setSelectedBusinessId,
  };
}

export function SupportConsole({ onSignOut }: { onSignOut: () => void }) {
  const operator = useQuery(api.support.currentOperator);

  if (operator === undefined) {
    return <SupportLoading />;
  }

  if (!operator.active) {
    return <SupportDenied onSignOut={onSignOut} />;
  }

  return (
    <ActiveSupportConsole
      onSignOut={onSignOut}
      operator={operator as SupportOperatorView}
    />
  );
}

function SupportLoading() {
  return (
    <main className="support-shell">
      <p className="eyebrow">NK35 Support</p>
      <h1>Checking access</h1>
    </main>
  );
}

function SupportDenied({ onSignOut }: { onSignOut: () => void }) {
  return (
    <main className="support-shell support-denied">
      <p className="eyebrow">NK35 Support</p>
      <h1>Support access denied</h1>
      <SignOutButton onSignOut={onSignOut} />
    </main>
  );
}

function ActiveSupportConsole({
  onSignOut,
  operator,
}: {
  onSignOut: () => void;
  operator: SupportOperatorView;
}) {
  const supportData = useSupportData(operator.active);

  return (
    <main className="support-shell">
      <SupportHeader onSignOut={onSignOut} operator={operator} />
      <section className="support-grid">
        <BusinessLookup {...supportData} />
        <SupportWorkspace {...supportData} />
      </section>
    </main>
  );
}

function SupportHeader({
  onSignOut,
  operator,
}: {
  onSignOut: () => void;
  operator: SupportOperatorView;
}) {
  return (
    <header className="support-header">
      <div>
        <p className="eyebrow">NK35 Support</p>
        <h1>Support console</h1>
        <span>{operatorName(operator)}</span>
      </div>
      <SignOutButton onSignOut={onSignOut} />
    </header>
  );
}

function SignOutButton({ onSignOut }: { onSignOut: () => void }) {
  return (
    <button onClick={onSignOut} type="button">
      <SignOut size={18} weight="bold" />
      Sign out
    </button>
  );
}

function BusinessLookup({
  activeBusinessId,
  overview,
  setSelectedBusinessId,
}: ReturnType<typeof useSupportData>) {
  return (
    <aside className="support-list" aria-label="Business lookup">
      {overview.map((item) => (
        <button
          aria-label={`Open ${item.business.name}`}
          className={
            item.business.businessId === activeBusinessId ? "selected" : ""
          }
          key={item.business.businessId}
          onClick={() => setSelectedBusinessId(item.business.businessId)}
          type="button"
        >
          <Briefcase size={20} weight="bold" />
          <span>{item.business.name}</span>
        </button>
      ))}
    </aside>
  );
}

function SupportWorkspace(data: ReturnType<typeof useSupportData>) {
  if (!data.selectedSummary) {
    return (
      <section className="support-main" aria-label="Support workspace">
        <section className="support-section">
          <h2>No businesses</h2>
        </section>
      </section>
    );
  }

  return (
    <section className="support-main" aria-label="Support workspace">
      <SupportMetrics
        recoveryCount={data.recoveryItems.length}
        summary={data.selectedSummary}
      />
      <RecoverySection recoveryItems={data.recoveryItems} />
      <ConversationSection conversations={data.conversations} />
      <SupportAuditSection auditEvents={data.auditEvents} />
    </section>
  );
}

function SupportMetrics({
  recoveryCount,
  summary,
}: {
  recoveryCount: number;
  summary: SupportOverviewItem;
}) {
  return (
    <div className="support-kpis">
      <SupportMetric
        icon={<ShieldCheck size={22} weight="bold" />}
        label="Account"
        value={`${summary.account.planKey} / ${summary.account.subscriptionStatus}`}
      />
      <SupportMetric
        icon={<PlugsConnected size={22} weight="bold" />}
        label="Integrations"
        value={`${summary.integrations.activeConnections} active`}
      />
      <SupportMetric
        icon={<WarningOctagon size={22} weight="bold" />}
        label="Recovery"
        value={`${recoveryCount} open`}
      />
    </div>
  );
}

function RecoverySection({
  recoveryItems,
}: {
  recoveryItems: SupportRecoveryItem[];
}) {
  const retryRecovery = useMutation(api.support.retryRecoveryItem);
  const deadLetterRecovery = useMutation(api.support.deadLetterRecoveryItem);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function handleRetry(item: SupportRecoveryItem) {
    await retryRecovery({
      businessId: item.businessId,
      targetKind: item.targetKind,
      targetId: item.targetId,
      idempotencyKey: makeRequestKey("retry", item),
    });
    setFeedback("Retry scheduled");
  }

  async function handleDeadLetter(item: SupportRecoveryItem) {
    await deadLetterRecovery({
      businessId: item.businessId,
      targetKind: item.targetKind,
      targetId: item.targetId,
      reason: "Marked from NK35 support console.",
      idempotencyKey: makeRequestKey("dead-letter", item),
    });
    setFeedback("Dead-letter recorded");
  }

  return (
    <section className="support-section">
      <h2>Recovery</h2>
      <SupportFeedback feedback={feedback} />
      <RecoveryRows
        onDeadLetter={handleDeadLetter}
        onRetry={handleRetry}
        recoveryItems={recoveryItems}
      />
    </section>
  );
}

function SupportFeedback({ feedback }: { feedback: string | null }) {
  return feedback ? <p className="support-feedback">{feedback}</p> : null;
}

function RecoveryRows({
  onDeadLetter,
  onRetry,
  recoveryItems,
}: {
  onDeadLetter: (item: SupportRecoveryItem) => Promise<void>;
  onRetry: (item: SupportRecoveryItem) => Promise<void>;
  recoveryItems: SupportRecoveryItem[];
}) {
  if (recoveryItems.length === 0) return <p>No recovery items</p>;

  return recoveryItems.map((item) => (
    <article className="support-row" key={item.targetId}>
      <div>
        <strong>{recoveryLabel(item)}</strong>
        <span>
          {item.status} / attempt {item.attempt} of {item.maxAttempts}
        </span>
        <small>{item.failureReason ?? "No failure reason"}</small>
      </div>
      <RecoveryActions
        item={item}
        onDeadLetter={onDeadLetter}
        onRetry={onRetry}
      />
    </article>
  ));
}

function RecoveryActions({
  item,
  onDeadLetter,
  onRetry,
}: {
  item: SupportRecoveryItem;
  onDeadLetter: (item: SupportRecoveryItem) => Promise<void>;
  onRetry: (item: SupportRecoveryItem) => Promise<void>;
}) {
  return (
    <div className="support-actions">
      <button
        aria-label={`Retry ${recoveryLabel(item)}`}
        onClick={() => void onRetry(item)}
        type="button"
      >
        <ArrowClockwise size={18} weight="bold" />
        Retry
      </button>
      <button
        aria-label={`Dead-letter ${recoveryLabel(item)}`}
        onClick={() => void onDeadLetter(item)}
        type="button"
      >
        <FirstAidKit size={18} weight="bold" />
        Dead-letter
      </button>
    </div>
  );
}

function ConversationSection({
  conversations,
}: {
  conversations: SupportConversationMetadata[];
}) {
  return (
    <section className="support-section">
      <h2>Conversations</h2>
      {conversations.map((conversation) => (
        <ConversationRow
          conversation={conversation}
          key={conversation.conversationId}
        />
      ))}
    </section>
  );
}

function ConversationRow({
  conversation,
}: {
  conversation: SupportConversationMetadata;
}) {
  return (
    <article className="support-row">
      <div>
        <strong>{conversation.conversationId.slice(-8)}</strong>
        <span>
          {conversation.state} / {conversation.ownershipState}
        </span>
        <small>
          {conversation.messageCount} messages /{" "}
          {conversation.unreadMessageCount} unread / last{" "}
          {formatShort(conversation.lastActivityAt)}
        </small>
      </div>
    </article>
  );
}

function SupportAuditSection({
  auditEvents,
}: {
  auditEvents: SupportAuditEvent[];
}) {
  if (auditEvents.length === 0) {
    return (
      <section className="support-section">
        <h2>Support audit</h2>
        <p>No support actions</p>
      </section>
    );
  }

  return (
    <section className="support-section">
      <h2>Support audit</h2>
      {auditEvents.map((event) => (
        <article
          className="support-row compact"
          key={event.supportAuditEventId}
        >
          <strong>{event.action}</strong>
          <span>{event.outcome}</span>
          <small>{formatShort(event.occurredAt)}</small>
        </article>
      ))}
    </section>
  );
}

function SupportMetric({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <article className="support-metric">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}
